import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

const ADMIN_IDLE_MS = 30 * 60 * 1000; // admins auto-logout after 30 min of inactivity
const TICK_MS = 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const lastActivity = useRef(Date.now());

  const checkAuth = useCallback(async () => {
    try {
      const u = await api.me();
      setUser(u);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = useCallback(async (username, password) => {
    const u = await api.login({ username, password });
    lastActivity.current = Date.now();
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    try { await api.logout(); } catch (e) { console.debug("logout request failed (ignored):", e); }
    setUser(false);
  }, []);

  // Track user activity (for admin inactivity auto-logout)
  useEffect(() => {
    const bump = () => { lastActivity.current = Date.now(); };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, bump));
  }, []);

  // Session maintenance: staff stay logged in (token kept fresh); admins auto-logout when idle.
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    const id = setInterval(async () => {
      const idle = Date.now() - lastActivity.current;
      if (user.role === "admin" && idle > ADMIN_IDLE_MS) {
        clearInterval(id);
        await logout();
        return;
      }
      try { await api.refresh(); } catch (e) { console.debug("token refresh failed; /me will reconcile:", e); }
    }, TICK_MS);
    return () => clearInterval(id);
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
